import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import postService from "./postService"

const initialState = {
  posts: {
    comments: [],
  },
  singlePost: {},
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: "",
}

/* getAllPosts */
export const getAllPosts = createAsyncThunk(
  "/posts/getAllPosts",
  async (_, thunkAPI) => {
    try {
      return postService.getAllPosts()
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* getPopularPosts */
export const getPopularPosts = createAsyncThunk(
  "/posts/getPopularPosts",
  async (_, thunkAPI) => {
    try {
      return postService.getPopularPosts()
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* getPost */
export const getPost = createAsyncThunk(
  "/posts/getPost",
  async (page, thunkAPI) => {
    try {
      return postService.getPost(page)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* getPostById */
export const getPostById = createAsyncThunk(
  "/posts/getPostById",
  async (id, thunkAPI) => {
    try {
      return postService.getPostById(id)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* getPostBySearch */
export const getPostBySearch = createAsyncThunk(
  "/posts/getPostBySearch",
  async (searchQuery, thunkAPI) => {
    try {
      return postService.getPostBySearch(searchQuery)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* createPost */
export const createPost = createAsyncThunk(
  "/posts/createPost",
  async (postData, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return postService.createPost(postData, token)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* updatePost */
export const updatePost = createAsyncThunk(
  "/posts/updatePost",
  async (postData, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return postService.updatePost(postData, token)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* deletePost */
export const deletePost = createAsyncThunk(
  "/posts/deletePost",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return postService.deletePost(id, token)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

/* likePost */
export const likePost = createAsyncThunk(
  "/posts/likePost",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return postService.likePost(id, token)
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

const postSlice = createSlice({
  name: "posts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      /* --------------- getAllPosts --------------- */
      .addCase(getAllPosts.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getAllPosts.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = action.payload.data
      })
      .addCase(getAllPosts.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- getPopularPosts --------------- */
      .addCase(getPopularPosts.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPopularPosts.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = action.payload.data
      })
      .addCase(getPopularPosts.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- getPost --------------- */
      .addCase(getPost.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = action.payload.data
        state.currentPage = action.payload.currentPage
        state.numberOfPages = action.payload.numberOfPages
      })
      .addCase(getPost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- getPostById --------------- */
      .addCase(getPostById.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPostById.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.singlePost = action.payload.post
        state.posts = action.payload.data
      })
      .addCase(getPostById.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- getPostBySearch --------------- */
      .addCase(getPostBySearch.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPostBySearch.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = action.payload
      })
      .addCase(getPostBySearch.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- createPost --------------- */
      .addCase(createPost.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts.push(action.payload)
      })
      .addCase(createPost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- updatePost --------------- */
      .addCase(updatePost.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = state.posts.map((post) =>
          post._id === action.payload._id ? action.payload : post
        )
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- deletePost --------------- */
      .addCase(deletePost.pending, (state) => {
        state.isLoading = true
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = state.posts.filter(
          (post) => post._id !== action.payload.id
        )
      })
      .addCase(deletePost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      /* --------------- likePost --------------- */
      .addCase(likePost.pending, (state) => {
        state.isLoading = true
      })
      .addCase(likePost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.posts = state.posts.map((post) =>
          post._id === action.payload._id ? action.payload : post
        )
      })
      .addCase(likePost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })
  },
})

// export const {} = postSlice.actions

export default postSlice.reducer
